// Identity guards for App-root pollers.
//
// TRAP(shell/poller-identity): a poller that calls setState with a FRESH array/object
// reference every tick — even when the content is identical — forces a full re-render
// of the ~88K-line App shell forever on every visible tab. Two unguarded pollers
// (`notif-calls-count` 15s, `timesheet-counts` 60s) did exactly that and were the
// app-side contributor to idle-tab renderer deaths ("Crashpad_HandlerDidNotRespond"
// after ~55 idle minutes). Every App-root poller MUST route its setState through an
// identity guard from this module (or an equivalent `prev`-preserving compare) so
// React can bail out when nothing actually changed. See docs/REGRESSIONS.md
// (2026-07-01) and the sibling TRAP(phone/sameidseq-flash) in src/App.tsx.
//
// All helpers are pure and side-effect free so they can be unit-tested (vitest).

/** Minimal structural shape for id-sequence comparison. */
type HasId = { id: unknown }

/**
 * True when two lists represent the same id sequence (same length, same `id` at
 * every index). Nullish-safe: two nullish inputs are "same"; a nullish vs a
 * non-nullish input is not. Used to keep the previous array reference when a
 * poll returns identical membership, so a state setter can return `prev`.
 *
 * Deliberate tradeoff (same as the phone panel's inline sameIdSeq): non-id field
 * changes on an existing member do not repaint until membership changes.
 */
export function sameIdSeq<T extends HasId>(
  a: readonly T[] | null | undefined,
  b: readonly T[] | null | undefined,
): boolean {
  if (a == null || b == null) return a == null && b == null
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.id !== b[i]?.id) return false
  }
  return true
}

/** Structural shape of the Timesheets sidebar-chip counts (api.ts TimeCountsByOwner). */
export type TimeCountsLike = {
  myPendingCount: number
  myApprovedTodayCount: number
  totalPendingIfAdmin?: number
}

/**
 * True when two timesheet count snapshots are equivalent. Nullish-safe:
 * two nullish inputs are "same" (fetchTimeCountsByOwner returns null on
 * 401/404); nullish vs non-nullish is not. `totalPendingIfAdmin` compares
 * strictly, so undefined (non-admin) vs 0 counts as a change.
 */
export function sameTimeCounts(
  a: TimeCountsLike | null | undefined,
  b: TimeCountsLike | null | undefined,
): boolean {
  if (a == null || b == null) return a == null && b == null
  if (a === b) return true
  return (
    a.myPendingCount === b.myPendingCount &&
    a.myApprovedTodayCount === b.myApprovedTodayCount &&
    a.totalPendingIfAdmin === b.totalPendingIfAdmin
  )
}

/** Minimal structural shape of a presence entry (App.tsx PresenceInfo). */
export type PresenceEntryLike = { state: string; tooltip: string }

/**
 * Merge a presence poll result into the previous presence map, PRESERVING the
 * previous object reference when nothing effectively changed.
 *
 * Semantics (kept identical to the previous inline merge in PresenceProvider):
 * - Only ids listed in `ids` are considered.
 * - An id missing from `incoming` keeps its previous value (no clobber to
 *   offline; unresolved new ids stay in the caller-computed 'loading' state).
 * - An entry without a string `state` is ignored.
 * - Falsy state coerces to 'offline'; falsy tooltip coerces to 'Offline'.
 *
 * Returning `prev` when no id's state/tooltip changed is what lets the
 * provider's context value keep its identity between real presence flips —
 * consumers are then notified by context-value change, not by App-shell churn.
 */
export function mergePresence<T extends PresenceEntryLike>(
  prev: Record<string, T>,
  ids: readonly string[],
  incoming: Record<string, { state?: string; tooltip?: string } | undefined> | null | undefined,
): Record<string, T> {
  if (!incoming) return prev
  let next: Record<string, T> | null = null
  for (const id of ids) {
    const p = incoming[id]
    if (!p || typeof p.state !== 'string') continue
    const state = p.state || 'offline'
    const tooltip = p.tooltip || 'Offline'
    const prevEntry = prev[id]
    if (prevEntry && prevEntry.state === state && prevEntry.tooltip === tooltip) continue
    if (!next) next = { ...prev }
    next[id] = { state, tooltip } as T
  }
  return next ?? prev
}
